import React, { useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import { type Size } from 'react-sizeme'
import HelpIcon from '@material-ui/icons/Help'
import Typography from '../Typography'
import StatCard from '../StatCard'
import DonutChart from '../GraphCard/DonutChart'
import Grid from '../Grid'
import GamifiedCallout from '../GamifiedCallout'
import Tooltip from '../Tooltip'
import type { StatCardGamifiedProps } from '../StatCardGamified'

const useStyles = makeStyles<Theme, StatCardProgressGamifiedProps>((theme: Theme) => ({
  root: (props: StatCardProgressGamifiedProps) => {
    return {
      width: '100%',
      boxSizing: 'border-box' as const,
      overflow: 'hidden',
      padding: `${theme.spacing(props.showGamifiedCallouts ? 3.8 : 3.9)}px ${theme.spacing(4)}px`,
      display: 'flex',
      paddingTop: theme.spacing(3.3),
      paddingBottom: theme.spacing(3.75),
      flexDirection: 'column',
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text.primary
    }
  },
  gamifiedCalloutWrapper: {
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2)
  },
  primaryValue: (props: StatCardProgressGamifiedProps) => {
    const length = (props.primaryValue ?? '').toString().length

    let fontSize = '2em'
    if (length >= 7) {
      fontSize = '1.2em'
    } else if (length >= 6) {
      fontSize = '1.4em'
    } else if (length >= 4) {
      fontSize = '1.5em'
    }

    return {
      fontVariantNumeric: 'tabular-nums',
      fontSize
    }
  },
  donutTitle: {
    textAlign: 'left',
    whiteSpace: 'nowrap',
    fontWeight: 400
  },
  verticalDivider: {
    marginTop: theme.spacing(1.5),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P90,
    width: 1,
    borderLeft: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRight: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    minHeight: '145px'
  },
  helpIcon: {
    color: theme.paintscout.palette.primary,
    fontSize: '1rem'
  },
  primaryTitle: {
    [theme.breakpoints.only('sm')]: {
      paddingLeft: 0,
      marginLeft: `-${theme.spacing(2)}px`
    },
    [theme.breakpoints.down(374)]: {
      paddingLeft: 0,
      marginLeft: `-${theme.spacing(3)}px`
    }
  },
  primaryDonut: {
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.only('sm')]: {
      marginLeft: `-${theme.spacing(2)}px`,
      paddingLeft: 0
    },
    [theme.breakpoints.down(374)]: {
      marginLeft: `-${theme.spacing(3)}px`,
      paddingLeft: 0
    }
  },
  secondaryDonut: {
    marginTop: theme.spacing(1.5)
  }
}))

export interface StatCardProgressGamifiedProps extends StatCardGamifiedProps {
  primaryValue: number
  secondaryValue: number
}

function StatCardProgressGamified(props: StatCardProgressGamifiedProps) {
  const classes = useStyles(props)
  const {
    loading,
    primaryTitle,
    primaryValue,
    secondaryTitle,
    secondaryValue,
    leaderboardRankData,
    showGamifiedCallouts,
    selectedEstimators,
    allEstimators,
    handleJoinClick
  } = props
  const donutSize: Size = {
    height: 110,
    width: 110
  }
  const [openGamifiedCollapse, setOpenGamifiedCollapse] = useState(false)
  const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1

  const cardContent = (
    <Grid container alignItems="flex-start" justifyContent="space-between" spacing={0}>
      <Grid item container direction="column" alignItems="center" xs={6} spacing={2}>
        <Grid item className={classes.primaryTitle}>
          <Tooltip
            icon={<HelpIcon fontSize="small" className={classes.helpIcon} />}
            interactive
            badgeRight={'-15%'}
            badgeTop="5%"
            content={<>The percentage of estimates that turn into accepted jobs.</>}
          >
            <Typography variant="h4" className={classes.donutTitle}>
              {primaryTitle}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item className={classes.primaryDonut}>
          <DonutChart loading={loading} value={primaryValue || 0} maxValue={100} size={donutSize} strokeWidth={13} />
        </Grid>
      </Grid>
      <div className={classes.verticalDivider} />
      <Grid item container direction="column" alignItems="center" xs={6} spacing={2}>
        <Grid item>
          <Tooltip
            icon={<HelpIcon fontSize="small" className={classes.helpIcon} />}
            interactive
            badgeRight={'-10%'}
            badgeTop="5%"
            content={<>The percentage of the total estimated value that has been accepted as jobs.</>}
          >
            <Typography variant="h4" className={classes.donutTitle}>
              {secondaryTitle}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item className={classes.secondaryDonut}>
          <DonutChart loading={loading} value={secondaryValue || 0} maxValue={100} size={donutSize} strokeWidth={13} />
        </Grid>
      </Grid>
      {showGamifiedCallouts && (
        <Grid item xs={12}>
          <div className={classes.gamifiedCalloutWrapper}>
            <GamifiedCallout
              topMargin={isFirefox ? 0 : 3}
              selectedEstimators={selectedEstimators}
              allEstimators={allEstimators}
              open={openGamifiedCollapse}
              onToggleCollapse={(_ev, open) => setOpenGamifiedCollapse((prev) => (open ? open : !prev))}
              type="winRate"
              data={leaderboardRankData}
              handleJoinClick={handleJoinClick}
            />
          </div>
        </Grid>
      )}
    </Grid>
  )

  return (
    <StatCard
      style={{
        minHeight: showGamifiedCallouts ? '285px' : '219px',
        maxHeight: showGamifiedCallouts ? (openGamifiedCollapse ? '397.5px' : '285px') : '219px'
      }}
      {...props}
      classes={classes}
      cardContent={cardContent}
      primaryTitle=""
    />
  )
}

export default StatCardProgressGamified
